import { isArray } from 'lodash';

import {
   Bean,
   createInterpolatedTranslationKey,
   TranslatableText,
   useTranslate,
   Validate,
   ValidateResult,
} from '@dreryk/react-common';
import {
   EmptyStatePlaceholder,
   FormContent,
   FormController,
   FormControllerRenderProps,
   Panel,
   Table,
   ValidationErrorNotification,
} from '@dreryk/react-components';

import { StyledFormSectionHeader } from '../../../common/StyledFormSectionHeader';
import { healthcarePracticeTranslationKeys } from '../../../translations';
import { HealthcarePracticeFormBean } from '../common/healthcarePractice';
import {
   isServiceProvidingLocationBeanInvalid,
   ServiceProvidingLocationFormBean,
} from '../common/serviceProvidingLocation';

import { HealthcarePracticeUpdateFormController } from './HealthcarePracticeUpdateForm.types';
import {
   useServiceProvidingLocationsToUpdateColumns,
   useServiceProvidingLocationsToUpdateController,
   useServiceProvidingLocationsToUpdateDataConfiguration,
} from './ServiceProvidingLocationsToUpdateTable.hooks';

type ServiceProvidingLocationsToUpdateTableProps = Pick<HealthcarePracticeUpdateFormController, 'register'> & {
   readonly healthcarePractice: HealthcarePracticeFormBean;
   readonly verifyAccountConfiguration: () => Promise<void>;
};

const isServiceProvidingLocationsExists = (data: unknown): data is ServiceProvidingLocationFormBean[] =>
   isArray(data) && Boolean(data.length);

const validateAllRequiredDataForServiceProvidingLocationsExists: Validate<Bean> = (data): ValidateResult => {
   if (isServiceProvidingLocationsExists(data)) {
      const serviceProvidingLocationWithoutAllRequiredData = data.filter(isServiceProvidingLocationBeanInvalid);

      if (serviceProvidingLocationWithoutAllRequiredData.length) {
         return createInterpolatedTranslationKey(
            healthcarePracticeTranslationKeys.noRequiredDataForServiceProvidingLocationFor,
            {
               locationCodes: serviceProvidingLocationWithoutAllRequiredData
                  .map((serviceProvidingLocation) => serviceProvidingLocation.locationCode)
                  .join(', '),
            }
         );
      }
   }

   return undefined;
};

type ServiceProvidingLocationsToUpdateTableRendererProps = FormControllerRenderProps<
   ServiceProvidingLocationFormBean[]
> & {
   readonly healthcarePractice: HealthcarePracticeFormBean;
   readonly verifyAccountConfiguration: () => Promise<void>;
};

const ServiceProvidingLocationsToUpdateTableRenderer = (
   props: ServiceProvidingLocationsToUpdateTableRendererProps
): JSX.Element => {
   const translate = useTranslate();
   const controller = useServiceProvidingLocationsToUpdateController(props);
   const dataConfiguration = useServiceProvidingLocationsToUpdateDataConfiguration(
      controller.serviceProvidingLocations
   );
   const columns = useServiceProvidingLocationsToUpdateColumns(
      controller,
      props.healthcarePractice,
      props.verifyAccountConfiguration
   );

   return (
      <Panel direction={'column'} testId={'serviceProvidingLocations'}>
         <FormContent testId={'serviceProvidingLocations'}>
            <StyledFormSectionHeader>
               {translate(healthcarePracticeTranslationKeys.takenFromServiceProvidingLocationRegistry)}
            </StyledFormSectionHeader>

            <Table<ServiceProvidingLocationFormBean>
               testId={'serviceProvidingLocationsToUpdate'}
               columns={columns}
               dataConfiguration={dataConfiguration}
               keyExtractor={'locationCode'}
               placeholder={
                  <EmptyStatePlaceholder
                     heading={healthcarePracticeTranslationKeys.noInfoServiceProvidingLocationFromRpwdl}
                     testId={'noInfoServiceLocationProviderToUpdate'}
                  />
               }
            />
            <ValidationErrorNotification label={props.error as TranslatableText} testId={'error'} />
         </FormContent>
      </Panel>
   );
};

export const ServiceProvidingLocationsToUpdateTable = (
   props: ServiceProvidingLocationsToUpdateTableProps
): JSX.Element => {
   return (
      <FormController<ServiceProvidingLocationFormBean[]>
         controller={props.register('serviceProvidingLocationsToUpdate', {
            validate: validateAllRequiredDataForServiceProvidingLocationsExists,
         })}
      >
         {(renderProps) => (
            <ServiceProvidingLocationsToUpdateTableRenderer
               {...renderProps}
               healthcarePractice={props.healthcarePractice}
               verifyAccountConfiguration={props.verifyAccountConfiguration}
            />
         )}
      </FormController>
   );
};
